import { ChallengeService } from '@duck/api-definitions'
import { PlayIcon } from '@heroicons/react/24/outline'
import { TranslationKey, useTar } from '@/modules'
import { Button } from '@duck/ui'
import { StandardLoader } from '../StandardLoader'
import { AllowedWeaponsPills } from '@components/AllowedWeaponsPills'
import { useOrgBranding } from '@/hooks/useOrgBranding'
import { getChallengeCover } from '@/utils/challengeCover.utils'
import { getAssignmentActionButton } from '@/utils/assignmentDisplay.utils'
import { useStartAssignment } from '@/hooks/useStartAssignment'

type Assignment = ChallengeService.AvailableAssignment

interface AssignmentChallengeCardProps {
  assignment: Assignment
  /** Opens the detail drawer. Card body click triggers this. */
  onOpenDetail?: (assignment: Assignment) => void
  /** This card's assignment is the one open in the detail drawer. */
  active?: boolean
}

/**
 * An assignment rendered with the exact look of a ChallengeCard — the only
 * difference is the cover: instead of an abstract motif it shows the assigning
 * organization's logo on the colored gradient. The body opens the detail drawer;
 * the action button boots the assignment directly.
 */
export const AssignmentChallengeCard: React.FC<AssignmentChallengeCardProps> = ({ assignment, onOpenDetail, active = false }) => {
  const { t } = useTar()
  const { requestStart, isLoading, timeLimitDialog } = useStartAssignment()
  const { branding: organization } = useOrgBranding(assignment.organizationId)

  const cover = getChallengeCover({ taskId: assignment.taskId })
  const actionButtonConfig = getAssignmentActionButton(assignment.status, assignment.startDate, assignment.endDate, t)

  const handleActionClick = () => {
    if (actionButtonConfig.disabled) return
    requestStart(assignment)
  }

  return (
    <>
      <div
        data-testid={`assignment-card-${assignment.taskId}`}
        role="button"
        tabIndex={0}
        onClick={() => onOpenDetail?.(assignment)}
        onKeyDown={(e) => { if (e.key === 'Enter') onOpenDetail?.(assignment) }}
        className={`group flex cursor-pointer flex-col overflow-hidden rounded-2xl border bg-card transition-transform hover:-translate-y-0.5 focus:outline-none focus-visible:outline-none ${active ? 'border-primary ring-2 ring-primary/40' : 'border-border hover:border-accent'}`}
      >
        {/* Cover: org logo on the colored gradient (in place of the abstract motif) */}
        <div className="relative h-28">
          <div className="absolute inset-0" style={{ background: cover.gradient }} />
          <div className="absolute inset-0 bg-gradient-to-b from-black/10 to-black/55" />
          <div className="absolute inset-0 z-10 grid place-items-center p-3">
            {organization?.logoURL ? (
              <img
                src={organization.logoURL}
                alt={assignment.organizationName}
                className="max-h-12 max-w-[60%] object-contain drop-shadow-[0_2px_6px_rgba(0,0,0,0.45)]"
              />
            ) : (
              <span className="line-clamp-2 text-center text-sm font-bold text-white/90 drop-shadow">
                {assignment.organizationName}
              </span>
            )}
          </div>
          <div className="absolute inset-x-2.5 top-2.5 z-10 flex items-center gap-1.5">
            <AllowedWeaponsPills allowedWeapons={assignment.allowedWeapons} />
          </div>
        </div>

        {/* Body — same structure as ChallengeCard */}
        <div className="flex flex-1 flex-col gap-2 p-4">
          <h3 className="line-clamp-2 text-base font-bold text-foreground">{assignment.name}</h3>
          <p className="line-clamp-2 text-sm text-muted-foreground">{assignment.teaser ?? assignment.description ?? ''}</p>

          <div className="mt-auto flex items-center gap-2 pt-1">
            <span className="truncate font-mono text-xs text-muted-foreground">{assignment.organizationName}</span>
            <Button
              data-testid="assignment-card-action-button"
              className={`ms-auto flex items-center justify-center gap-2 ${!isLoading ? actionButtonConfig.className : ''}`}
              variant={isLoading ? 'outline' : 'default'}
              onClick={(e) => { e.stopPropagation(); handleActionClick() }}
              disabled={isLoading || actionButtonConfig.disabled}
            >
              {isLoading ? (
                <StandardLoader size="md" inline />
              ) : (
                <>
                  <PlayIcon className="w-4 h-4" />
                  <span>{actionButtonConfig.isCustomText ? actionButtonConfig.text : t(actionButtonConfig.text as TranslationKey)}</span>
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {timeLimitDialog}
    </>
  )
}

export type { Assignment, AssignmentChallengeCardProps }
